import { useCallback, useEffect, useState } from 'react'
import {
  ApiError, bridge,
  type BridgeCertInfo, type BridgeDriverInfo, type BridgeStatus,
} from '../lib/api'
import { signCapabilities, type SignCompleteResponse, type SignPrepareResponse } from '../lib/portal'
import { SITE } from '../lib/site'
import SecretInput from './SecretInput'

interface SignPanelProps {
  /**
   * Phase 1: send the signer's Base64-DER certificate; the server auto-selects the highest
   * available signature level (long-term when a TSA + revocation infrastructure are configured).
   * `timestamp` / `longTerm` are the request opt-ins the panel threads from server capabilities.
   */
  prepare:  (signerCertificateB64: string, timestamp: boolean, longTerm: boolean) => Promise<SignPrepareResponse>
  /** Phase 2: send the raw PKCS#1 signature, get the final status. */
  complete: (sessionId: string, rawSignatureB64: string) => Promise<SignCompleteResponse>
  onSuccess: (result: SignCompleteResponse) => void
  /**
   * Whether the document being signed is a PDF. Drives the level banner honestly per document type:
   * a PDF is signed at PAdES — PAdES-B-LT (long-term, DSS/VRI) when the server has the LTV enricher,
   * otherwise PAdES-T / PAdES-B-B — while a non-PDF becomes CAdES-ESXLong / CAdES-T / CAdES-BES. The
   * PDF long-term claim is gated on the server's `pdfLongTermAvailable`, not the CAdES flag.
   */
  isPdf?: boolean
}

/** Internal props for the two sub-flows: the panel-level level choice is threaded down. */
interface SubFlowProps extends SignPanelProps {
  timestamp: boolean
  longTerm:  boolean
}

type Mode = 'token' | 'pfx'

/** Small radio-style selection indicator used in the token picker list. */
function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden
      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
        selected ? 'border-[var(--color-accent)]' : 'border-[var(--color-border)]'
      }`}
    >
      {selected && <span className="h-2 w-2 rounded-full bg-[var(--color-accent)]" />}
    </span>
  )
}

/**
 * Reusable signing widget for the portal: drives Bridge (USB token) or a PFX fallback
 * through the server-side hash-only prepare/complete contract. The document stays on the
 * server; only DataToSign and the raw signature cross the wire.
 */
export default function SignPanel({ prepare, complete, onSuccess, isPdf = false }: SignPanelProps) {
  const [mode, setMode] = useState<Mode>('token')
  const [tsAvailable, setTsAvailable] = useState(false)
  const [ltAvailable, setLtAvailable] = useState(false)
  const [pdfLtAvailable, setPdfLtAvailable] = useState(false)

  // Probe server capabilities to describe the auto-selected signature level to the user. The level
  // itself is always driven server-side: the panel requests the maximum (longTerm) and the server
  // clamps to what it can produce (ESXLong → T → BES), so signing never fails for lack of a TSA.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const caps = await signCapabilities()
        if (!cancelled) {
          setTsAvailable(caps.timestampAvailable)
          setLtAvailable(caps.longTermAvailable)
          setPdfLtAvailable(caps.pdfLongTermAvailable)
        }
      } catch {
        if (!cancelled) { setTsAvailable(false); setLtAvailable(false); setPdfLtAvailable(false) }
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Auto-select the highest level: always request long-term; the server degrades gracefully.
  const longTerm  = true
  const timestamp = false

  // Honest per-document level copy, reflecting the actual server capability. A PDF is signed at
  // PAdES: with long-term infrastructure it becomes PAdES-B-LT (DSS/VRI validation material embedded
  // at the PDF level), otherwise PAdES-T or PAdES-B-B. Non-PDF documents become CAdES-ESXLong /
  // CAdES-T / CAdES-BES. The "valid after the certificate expires" claim is shown only when the
  // produced level actually carries the long-term validation material.
  const level = isPdf
    ? pdfLtAvailable
      ? { title: 'Uzun dönemli ve zaman damgalı imza',
          desc:  'PDF için PAdES-LT üretilir; TÜBİTAK Kamu SM zaman damgasının yanı sıra sertifika ve iptal bilgileri belgeye (DSS/VRI) gömülür. İmza, sertifikanızın süresi dolduktan sonra da doğrulanabilir kalır.' }
      : tsAvailable
        ? { title: 'Zaman damgalı imza (PAdES-T)',
            desc:  'PDF için PAdES imza üretilir; imzaya TÜBİTAK Kamu SM zaman damgası eklenir.' }
        : { title: 'Elektronik imza (PAdES)',
            desc:  'Belgeniz sunucuda kalır; yalnızca imzalanacak özet ve ham imza taşınır.' }
    : ltAvailable
      ? { title: 'Uzun dönemli ve zaman damgalı imza',
          desc:  'CAdES-ESXLong üretilir; TÜBİTAK Kamu SM zaman damgası ile sertifika ve iptal bilgileri imzaya gömülür. İmza, sertifikanızın süresi dolduktan sonra da doğrulanabilir kalır.' }
      : tsAvailable
        ? { title: 'Zaman damgalı imza (CAdES-T)',
            desc:  'İmzaya TÜBİTAK Kamu SM zaman damgası eklenir; imza tarihi üçüncü tarafça ispatlanabilir olur.' }
        : { title: 'Elektronik imza',
            desc:  'Belgeniz sunucuda kalır; yalnızca imzalanacak özet ve ham imza taşınır.' }

  return (
    <div>
      <div className="inline-flex rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-1">
        <button
          type="button"
          onClick={() => setMode('token')}
          className={`rounded px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === 'token' ? 'bg-white text-[var(--color-fg)] shadow-sm' : 'text-[var(--color-fg-muted)]'
          }`}
        >
          USB Token (Önerilen)
        </button>
        <button
          type="button"
          onClick={() => setMode('pfx')}
          className={`rounded px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === 'pfx' ? 'bg-white text-[var(--color-fg)] shadow-sm' : 'text-[var(--color-fg-muted)]'
          }`}
        >
          PFX / Gelişmiş İmza
        </button>
      </div>

      {/* Auto-level banner: the produced signature is always the strongest the server supports. */}
      <div className="mt-4 flex items-start gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-3">
        <span
          aria-hidden
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)] text-[11px] font-bold text-white"
        >✓</span>
        <span className="text-sm">
          <span className="block font-medium">{level.title}</span>
          <span className="block text-xs text-[var(--color-fg-muted)]">{level.desc}</span>
        </span>
      </div>

      <div className="mt-6">
        {mode === 'token'
          ? <TokenSign prepare={prepare} complete={complete} onSuccess={onSuccess} timestamp={timestamp} longTerm={longTerm} />
          : <PfxSign prepare={prepare} complete={complete} onSuccess={onSuccess} timestamp={timestamp} longTerm={longTerm} />}
      </div>
    </div>
  )
}

// ── Token (Bridge) flow ─────────────────────────────────────────────────────────

type TokenStatus = 'probing' | 'no-bridge' | 'tokens-loaded' | 'pin-entry' | 'cert-list' | 'signing' | 'error'

interface TokenSelection { driverPath: string; driverName: string; slotId: number; label: string }

function TokenSign({ prepare, complete, onSuccess, timestamp, longTerm }: SubFlowProps) {
  const [status, setStatus]           = useState<TokenStatus>('probing')
  const [bridgeInfo, setBridgeInfo]   = useState<BridgeStatus | null>(null)
  const [drivers, setDrivers]         = useState<BridgeDriverInfo[]>([])
  const [tokensMessage, setTokensMessage] = useState<string | null>(null)
  const [selectedTok, setSelectedTok] = useState<TokenSelection | null>(null)
  const [pin, setPin]                 = useState('')
  const [certs, setCerts]             = useState<BridgeCertInfo[]>([])
  const [selectedCert, setSelectedCert] = useState<BridgeCertInfo | null>(null)
  const [error, setError]             = useState<string | null>(null)
  const [progress, setProgress]       = useState('')
  const [waking, setWaking]           = useState(false)

  const probe = useCallback(async () => {
    setStatus('probing'); setError(null)
    try {
      const st = await bridge.probe()
      if (!st) { setStatus('no-bridge'); return }
      setBridgeInfo(st)
      const tokens = await bridge.listTokensFull()
      setDrivers(tokens.drivers)
      setTokensMessage(tokens.message)
      // Auto-select when there is exactly one token — skip manual selection.
      const flat = tokens.drivers.flatMap(d => d.tokens.map(t => ({ d, t })))
      if (flat.length === 1) {
        const { d, t } = flat[0]
        setSelectedTok({ driverPath: d.path, driverName: d.driver, slotId: t.slotId, label: t.label })
        setPin(''); setCerts([]); setSelectedCert(null)
        setStatus('pin-entry')
      } else {
        setStatus('tokens-loaded')
      }
    } catch {
      setStatus('no-bridge')
    }
  }, [])

  useEffect(() => { void probe() }, [probe])

  // "Uygulamayı başlat": kurulu ama çalışmayan köprüyü wsign://wake ile uyandır,
  // ayağa kalkınca tam probe akışını (token listesi) yeniden çalıştır.
  const wake = useCallback(async () => {
    setWaking(true); setError(null)
    try {
      const st = await bridge.wakeAndWait()
      if (st) { await probe(); return }
      setError('W.Sign uygulaması başlatılamadı. Kurulu değilse aşağıdan indirin; kuruluysa elle açıp "Tekrar dene"ye basın.')
    } finally {
      setWaking(false)
    }
  }, [probe])

  const submitPin = useCallback(async () => {
    if (!selectedTok || !pin) return
    setStatus('signing'); setProgress("Sertifikalar yükleniyor… (PIN doğrulanıyor)"); setError(null)
    try {
      const list = await bridge.listCertificates({ driverPath: selectedTok.driverPath, slotId: selectedTok.slotId, pin })
      setCerts(list)
      // Auto-select when there is exactly one certificate.
      setSelectedCert(list.length === 1 ? list[0] : null)
      setStatus('cert-list'); setProgress('')
    } catch (err) {
      setStatus('pin-entry'); setProgress('')
      setError(errMsg(err))
    }
  }, [selectedTok, pin])

  const signNow = useCallback(async () => {
    if (!selectedTok || !selectedCert) return
    setStatus('signing'); setError(null)
    try {
      setProgress('Sunucu imza gövdesini hazırlıyor…')
      const prep = await prepare(selectedCert.certificateB64, timestamp, longTerm)

      setProgress('Token imzalıyor… (PIN doğrulandı)')
      const signed = await bridge.signHash({
        driverPath:       selectedTok.driverPath,
        slotId:           selectedTok.slotId,
        pin,
        ckaId:            selectedCert.ckaId,
        dataToSignBase64: prep.dataToSign,
      })

      setProgress('Sunucu imzayı tamamlıyor…')
      const result = await complete(prep.sessionId, signed.rawSignature)
      setProgress('')
      onSuccess(result)
    } catch (err) {
      setStatus('cert-list'); setProgress('')
      setError(errMsg(err))
    }
  }, [selectedTok, selectedCert, pin, prepare, complete, onSuccess, timestamp, longTerm])

  if (status === 'probing')
    return <div className="card"><p className="text-sm text-[var(--color-fg-muted)]">Yerel W.Sign köprüsü aranıyor…</p></div>

  if (status === 'no-bridge')
    return (
      <div className="card border-blue-200 bg-blue-50">
        <h3 className="text-lg font-semibold text-blue-900">W.Sign yardımcı uygulaması gerekli</h3>
        <p className="mt-2 text-sm text-blue-800 max-w-2xl">
          Tarayıcı USB token'a doğrudan erişemez; imzalama yerel W.Sign uygulaması üzerinden yapılır.
          <strong> Uygulama zaten kuruluysa "Uygulamayı başlat"a basın</strong> — kurulu değilse aşağıdan
          indirin. Token'ınız yoksa <strong>PFX Dosyası</strong> seçeneğini kullanın.
        </p>
        <p className="mt-2 text-xs text-blue-700 max-w-2xl">
          İlk başlatmada tarayıcınız W.Sign'ı açmak için izin isteyebilir — "Aç"a basın.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button onClick={wake} disabled={waking} type="button" className="btn-primary">
            {waking ? 'Başlatılıyor…' : 'Uygulamayı başlat'}
          </button>
          <a href={SITE.downloadUrl} className="btn-ghost">
            W.Sign kur (Windows) →
          </a>
          <button onClick={probe} disabled={waking} type="button" className="btn-ghost">Tekrar dene</button>
        </div>
        {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      </div>
    )

  const flatTokens = drivers.flatMap(d => d.tokens.map(t => ({ d, t })))

  return (
    <div className="space-y-4">
      {bridgeInfo && (
        <div className="card border-emerald-200 bg-emerald-50">
          {/* End-user surface: keep it reassuring, not technical. The base URL + version
              stay in the title tooltip so support can still read them on hover. */}
          <p className="text-sm text-emerald-900" title={`${bridgeInfo.bridgeBaseUrl} · v${bridgeInfo.version}`}>
            <strong>W.Sign masaüstü uygulaması bağlı.</strong> Kartınızla güvenle imzalayabilirsiniz.
          </p>
        </div>
      )}

      {/* No token/reader: prominent warning (driver may be installed but nothing plugged in) */}
      {flatTokens.length === 0 ? (
        <div className="card border-amber-200 bg-amber-50">
          <h3 className="font-semibold text-amber-900">Kart/token takılı değil</h3>
          <p className="mt-2 text-sm text-amber-800 max-w-2xl">
            {tokensMessage ?? 'Nitelikli e-imza USB token\'ınızı bilgisayara takın; ardından "Tekrar tara"ya basın. Kart okuyucunuz bağlı ve sürücüsü kurulu olmalı.'}
          </p>
          {drivers.length > 0 && (
            <p className="mt-2 text-xs text-amber-700">
              Bulunan sürücü: {drivers.map(d => d.driver).join(', ')} — ama takılı token görünmüyor.
            </p>
          )}
          <button onClick={probe} type="button" className="btn-ghost mt-3">Tekrar tara</button>
        </div>
      ) : (
        <div className="card">
          <h3 className="font-semibold">1. Token seç</h3>
          {flatTokens.length === 1 && (
            <p className="mt-1 text-xs text-[var(--color-fg-muted)]">Tek token bulundu, otomatik seçildi.</p>
          )}
          <div className="mt-3 space-y-2">
            {flatTokens.map(({ d, t }, i) => {
              const isSel = selectedTok?.driverPath === d.path && selectedTok?.slotId === t.slotId
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setSelectedTok({ driverPath: d.path, driverName: d.driver, slotId: t.slotId, label: t.label }); setPin(''); setCerts([]); setSelectedCert(null); setStatus('pin-entry'); setError(null) }}
                  className={`flex w-full items-start gap-3 rounded border p-3 text-left text-sm ${
                    isSel ? 'border-[var(--color-accent)] bg-[var(--color-accent-wash)]' : 'border-[var(--color-border)] hover:bg-[var(--color-bg-soft)]'
                  }`}
                >
                  <RadioDot selected={isSel} />
                  <span>
                    <span className="block font-medium">{t.label || '(etiketsiz token)'}</span>
                    <span className="block text-xs text-[var(--color-fg-muted)]">{d.driver} · {t.manufacturer} {t.model} · slot {t.slotId}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {selectedTok && (status === 'pin-entry' || status === 'cert-list' || status === 'signing') && (
        <div className="card">
          <h3 className="font-semibold">2. PIN</h3>
          {status === 'pin-entry' ? (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
              <SecretInput
                value={pin} autoFocus
                onChange={e => setPin(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void submitPin() }}
                placeholder="••••"
                className="flex-1 rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
              />
              <button onClick={submitPin} disabled={!pin} type="button" className="btn-primary">Sertifikaları getir</button>
            </div>
          ) : (
            <p className="mt-2 text-sm text-emerald-700">PIN doğrulandı · {certs.length} sertifika</p>
          )}
        </div>
      )}

      {(status === 'cert-list' || status === 'signing') && certs.length > 0 && (
        <div className="card">
          <h3 className="font-semibold">3. Sertifika ve imza</h3>
          <div className="mt-3 space-y-2">
            {certs.map((c, i) => {
              const isSel = selectedCert?.ckaId === c.ckaId
              return (
                <button
                  key={i} type="button" onClick={() => setSelectedCert(c)}
                  className={`block w-full rounded border p-3 text-left text-sm ${
                    isSel ? 'border-[var(--color-accent)] bg-[var(--color-accent-wash)]' : 'border-[var(--color-border)] hover:bg-[var(--color-bg-soft)]'
                  }`}
                >
                  <p className="font-medium">{c.subject}</p>
                  <p className="text-xs text-[var(--color-fg-muted)]">İhraç: {c.issuer}</p>
                </button>
              )
            })}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button onClick={signNow} disabled={!selectedCert || status === 'signing'} type="button" className="btn-primary">
              {status === 'signing' ? 'İmzalanıyor…' : 'İmzala'}
            </button>
            {progress && <span className="text-xs text-[var(--color-fg-muted)]">{progress}</span>}
          </div>
        </div>
      )}

      {error && (
        <div className="card border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
    </div>
  )
}

// ── PFX fallback ─────────────────────────────────────────────────────────────────

type PfxStatus = 'idle' | 'unlocked' | 'signing' | 'error'

function PfxSign({ prepare, complete, onSuccess, timestamp, longTerm }: SubFlowProps) {
  const [status, setStatus]   = useState<PfxStatus>('idle')
  const [buffer, setBuffer]   = useState<Uint8Array | null>(null)
  const [fileName, setFileName] = useState('')
  const [password, setPassword] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [pfx, setPfx]         = useState<any>(null)
  const [error, setError]     = useState<string | null>(null)
  const [progress, setProgress] = useState('')

  const unlock = useCallback(async () => {
    if (!buffer || !password) return
    setError(null)
    try {
      const { parsePfx } = await import('../lib/pfx')
      setPfx(await parsePfx(buffer, password))
      setStatus('unlocked')
    } catch (err) {
      setError(errMsg(err))
    }
  }, [buffer, password])

  const signNow = useCallback(async () => {
    if (!pfx) return
    setStatus('signing'); setError(null)
    try {
      const { bytesToBase64, signPkcs1Sha256 } = await import('../lib/pfx')
      setProgress('Sunucu imza gövdesini hazırlıyor…')
      const prep = await prepare(bytesToBase64(pfx.certificateDer), timestamp, longTerm)

      setProgress('Yerel imzalanıyor (private key tarayıcıdan çıkmaz)…')
      const dataToSign = base64ToUint8(prep.dataToSign)
      const rawSig = await signPkcs1Sha256(pfx.privateKey, dataToSign)

      setProgress('Sunucu imzayı tamamlıyor…')
      const result = await complete(prep.sessionId, bytesToBase64(rawSig))
      setProgress('')
      onSuccess(result)
    } catch (err) {
      setStatus('unlocked'); setProgress('')
      setError(errMsg(err))
    }
  }, [pfx, prepare, complete, onSuccess, timestamp, longTerm])

  return (
    <div className="space-y-4">
      <div className="card border-amber-200 bg-amber-50">
        <p className="text-sm text-amber-800">
          Bu yöntem <strong>5070 sayılı Kanun kapsamında nitelikli (güvenli) elektronik imza üretmez</strong> —
          ıslak imza eşdeğeri değildir. Kamu kurumları, mahkemeler ve e-devlet sistemleri kabul etmez.
          Yasal geçerlilik için USB token kullanın. PFX, geliştirici testi/entegrasyon için uygundur.
          Şifre ve private key sunucuya gitmez.
        </p>
      </div>
      <div className="card">
        {!pfx ? (
          <>
            <input
              type="file" accept=".pfx,.p12"
              onChange={async e => { const f = e.target.files?.[0]; if (f) { setFileName(f.name); setBuffer(new Uint8Array(await f.arrayBuffer())) } }}
              className="block text-sm"
            />
            {buffer && (
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
                <SecretInput
                  value={password} autoFocus placeholder="PFX şifresi"
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') void unlock() }}
                  className="flex-1 rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
                />
                <button onClick={unlock} disabled={!password} type="button" className="btn-primary">Aç</button>
              </div>
            )}
            {fileName && <p className="mt-2 text-xs font-mono text-[var(--color-fg-dim)]">{fileName}</p>}
          </>
        ) : (
          <>
            <p className="text-sm font-medium">{pfx.subject}</p>
            <div className="mt-3 flex items-center gap-3">
              <button onClick={signNow} disabled={status === 'signing'} type="button" className="btn-primary">
                {status === 'signing' ? 'İmzalanıyor…' : 'İmzala'}
              </button>
              {progress && <span className="text-xs text-[var(--color-fg-muted)]">{progress}</span>}
            </div>
          </>
        )}
      </div>
      {error && <div className="card border-red-200 bg-red-50"><p className="text-sm text-red-700">{error}</p></div>}
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function errMsg(err: unknown): string {
  if (err instanceof ApiError) return `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message
  return String(err)
}

function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}
